import { DateHelper, Popup } from "@bryntum/gantt";
import css from "../../../css/modules/toolbar.module.scss";
import { translate } from "../../../utils/translate";
import { singleSettingSelector } from "../../../redux/setting";
import { Button, FormControl, FormControlLabel, Switch, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { usePageApi } from "./PageApi";
import { ToggleButton } from '@mui/material';
import { useSelector } from "react-redux";
import { Features } from "../../../components/Toolbar/Features";
import { SaveSettings } from "../../../components/Toolbar/SaveSettings";
import clsx from "clsx";
import { observer } from "../../../utils/observer";
import dayjs from 'dayjs';
import { snackbar } from "../../../redux/snackbar";
import { apiUrls } from "../../../utils/api";
import { BaselineButtons } from "./BaselineButtons";
import { ExportButton } from "./ExportButton";
import { unsavedChangesPopup } from "../utils/utils";
import { ViewPresetSelect } from "../../../components/Toolbar/ViewPresetSelect";
import { UsersOnPageBtn } from "../../../components/Toolbar/UsersOnPage";
import { SearchTask } from "../../../components/Toolbar/SearchTask";
import { groupNames } from "../../Settings/components/From/utils";
import { ToggleVisibilityButtons } from "../../../components/Toolbar/ToggleVisibilityButtons";
import { AccessEntries, FEATURE_ACCESS } from "../../../utils/accessEntries";
import { useApiClient } from '../../../components/ApiProvider';
import { BryntumDateField } from "@bryntum/gantt-react";

export function ToolBar({ overview, readOnly }) {
  const { canSave, autoSaveEnabled, update, api, users } = usePageApi();
  const onAutoSaveToggle = useCallback(
    (e, checked) => {
      update({
        autoSaveEnabled: checked,
      });
    },
    [update]
  );
  const configs = useSelector(singleSettingSelector);
  const autoSaveVisible = configs.autoSave || false;
  const onExpand = useCallback(() => api.gantt.expandAll(), [api]);
  const apiClient = useApiClient();
  const onNext = useCallback(async () => {
    const x = api.activeElement?.timeAxisSubGrid?.scrollable?.scrollWidth;
    if (x) {
      await api.activeElement?.shiftNext();
      await api.activeElement?.scrollTo(x);
    }
  }, [api]);
  const onPrev = useCallback(async () => {
    await api.activeElement?.shiftPrevious();
    await api.activeElement?.scrollTo(0);
  }, [api]);
  const onCollapse = useCallback(() => api.gantt.collapseAll(), [api]);
  const onZoomIn = useCallback(() => api.activeElement.zoomIn(), [api]);
  const onZoomOut = useCallback(() => api.activeElement.zoomOut(), [api]);
  const onZoomToFit = useCallback(() => {
    try {
      api.activeElement.zoomToFit({
        leftMargin: 50,
        rightMargin: 50,
      });
    } catch (err) {
      console.log("zoomToFit is not working on ", api.activeElement?.constructor?.name);
    }
  }, [api]);
  const onPresetClick = useCallback(
    (id) => {
      if (api.activeElement) {
        api.activeElement.viewPreset = id;
        api.activeElement.renderContents(); //Needed because sometimes the changed preset is not seen as a change and not rendered
      }
    },
    [api]
  );
  const onSync = useCallback(() => {
    observer.trigger("SyncProject");
  }, []);
  const onCriticalPath = useCallback(() => {
    try {
      api.gantt.features.criticalPaths.disabled = !api.gantt.features.criticalPaths.disabled;
    } catch (e) { /* empty */ }
  }, [api]);

  const onProjectStartChange = useCallback(
    (date) => {
      if (!api?.gantt?.project || !overview?.state?.currentProject?.ProjectIdString || !apiClient) {
        return;
      }
      api.gantt.startDate = DateHelper.add(date, -1, "week");
      const params = new URLSearchParams({
        projectID: overview.state.currentProject.ProjectIdString,
        projectStart: date.toISOString(),
      });
      api.gantt.project.setStartDate(date);
      apiClient.post(apiUrls.UpdateProjectStartDate + "?" + params.toString());
    },
    [api, overview?.state?.currentProject?.ProjectIdString, apiClient]
  );

  const startDateFieldRef = useRef(null);
  useEffect(() => {
    if (startDateFieldRef.current?.instance && api?.gantt?.project?.startDate) {
      const newDate = api.gantt.project.startDate instanceof Date 
        ? api.gantt.project.startDate 
        : new Date(api.gantt.project.startDate);
      startDateFieldRef.current.instance.value = newDate;
    }
  }, [api?.gantt?.project?.startDate]);

  const processProgress = async () => {
    const projectId = overview?.state?.currentProject?.ProjectIdString;
    let save = false;
    let ignore = false;
    if (overview.state.hasChanges) {
      const res = await unsavedChangesPopup(translate("processProgressUnsavedContent"));
      if (res.cancel) {
        return;
      }
      save = res.save;
      ignore = res.ignore;
    }

    const popup = new Popup({
      width: 400,
      title: translate("processProgressTitle"),
      modal: {
        closeOnMaskTap: true,
      },
      centered: true,
      closeAction: "destroy",
      closable: true,
      content: `<div style='width:100%;margin-bottom: 20px'>${translate("processProgressContent")}</div>`,
      items: [
        {
          ref: "dateField",
          type: "date",
          editable: false,
          required: true,
          placeholder: translate("processProgressInputPlaceholder"),
        },
      ],
      bbar: [
        {
          type: "button",
          text: translate("Cancel"),
          style: "min-width: 30%;margin-right:auto;",
          color: "b-raised",
          onClick: () => {
            popup.close();
          },
        },
        {
          type: "button",
          ref: "saveButton",
          text: translate("processProgressGoBtn"),
          style: "min-width: 30%",
          color: "b-raised",
          async onClick() {
            const field = this.parent.parent.widgetMap.dateField;
            if (field.isValid) {
              this.disabled = true;
              const params = new URLSearchParams();
              params.set("project", projectId);
              params.set("progressDate", field.value.toISOString());
              if (save) {
                await overview.saveProject();
              }
              if (ignore) {
                await new Promise((res) => overview.setState({ hasChanges: false }, res));
              }
              try {
                const stream = await apiClient.post(apiUrls.ProcessProgress + "?" + params.toString());
                const changedTasks = await stream.json();
                if (changedTasks) {
                  changedTasks.forEach((task) => {
                    let taskInProject = api.gantt.project.taskStore.getById(task.id);
                    if (taskInProject) {
                      taskInProject.setStartDate(new Date(task.startDate), true);
                      taskInProject.setEndDate(new Date(task.endDate), true);
                      taskInProject.setEffort(task.effort);
                      taskInProject.ActualHours = task.ActualHours;
                      taskInProject.percentDone = task.percentDone;
                    }
                  });
                  await api.gantt.project.taskStore.commitAsync();
                }
              } catch (err) {
                console.error(err);
                overview.props.dispatch(
                  snackbar.actions.add({
                    id: "processProgressError",
                    severity: "warning",
                    message: translate("processProgressError"),
                    autoHideDuration: 4000,
                  })
                );
              } finally {
                popup.close();
              }
            }
          },
        },
      ],
    });
  };
  const setting = useSelector((state) => state.setting);
  const allowBulldoze = setting.user?.accessRights[AccessEntries.ProcessProgress]?.AccessLevel === FEATURE_ACCESS.Enabled;


  return (
    <div className={css.singleToolbar}>
      <div className={css.btnContainer}>
        {!readOnly && (
          <Tooltip title={translate("Save changes")}>
            <span>
              <ToggleButton
                data-testid="save-changes-button"
                selected={canSave}
                disabled={!canSave}
                value="save"
                onClick={overview.saveProject}
                size="small"
                className={css.iconBtn}
              >
                <span className="ico icon-floppy-disk" />
              </ToggleButton>
            </span>
          </Tooltip>
        )}
        {!!autoSaveVisible && !readOnly && (
          <FormControl className={css.formControl}>
            <FormControlLabel
              control={<Switch color="primary" checked={autoSaveEnabled} onChange={onAutoSaveToggle} name="autoSave" />}
              label="Auto save"
            />
          </FormControl>
        )}
        <Tooltip title={translate("Expand all")}>
          <Button onClick={onExpand} size="small" className={css.iconBtn} data-testid="expand-button">
            <span className="ico icon-rank2 deg-180" />
          </Button>
        </Tooltip>
        <Tooltip title={translate("Collapse all")}>
          <Button onClick={onCollapse} size="small" className={css.iconBtn} data-testid="collapse-button">
            <span className="ico icon-rank2" />
          </Button>
        </Tooltip>
        <ViewPresetSelect onChange={onPresetClick} api={api} />
        <Tooltip title={translate("Zoom in")}>
          <Button onClick={onZoomIn} size="small" className={css.iconBtn} data-testid="zoom-in-button">
            <span className="ico icon-zoom-in" />
          </Button>
        </Tooltip>
        <Tooltip title={translate("Zoom out")}>
          <Button onClick={onZoomOut} size="small" className={css.iconBtn} data-testid="zoom-out-button">
            <span className="ico icon-zoom-out" />
          </Button>
        </Tooltip>
        <Tooltip title={translate("Zoom to fit")}>
          <Button onClick={onZoomToFit} size="small" className={css.iconBtn} data-testid="zoom-to-fit-button">
            <span className="ico icon-contract" />
          </Button>
        </Tooltip>
        <Tooltip title={translate("Previous time span")}>
          <Button onClick={onPrev} size="small" className={css.iconBtn} data-testid="prev-time-span-button">
            <span className="ico icon-chevron-left" />
          </Button>
        </Tooltip>
        <Tooltip title={translate("Next time span")}>
          <Button onClick={onNext} size="small" className={css.iconBtn} data-testid="next-time-span-button">
            <span className="ico icon-chevron-right" />
          </Button>
        </Tooltip>
        <Features api={api} />
        <Tooltip title={translate("Highlight critical paths")}>
          <ToggleButton
            data-testid="highlight-button"
            selected={!(api.gantt?.features?.criticalPaths?.disabled || false)}
            onChange={onCriticalPath}
            size="small"
            value="criticalPath"
            className={css.iconBtn}
          >
            <span className="ico icon-fire" />
          </ToggleButton>
        </Tooltip>
        <SearchTask api={api} />
        <BryntumDateField  
          ref={startDateFieldRef}
          labelPosition="above"
          labelCls={clsx(css.muiDatepickerUiLabel)}
          label={translate("Project start")}
          data-testid="start-date-field"
          cls={clsx(css.formControl, css.input, css.muiDatepickerUi)}
          format="ll"
          date={api?.gantt?.project?.startDate ? dayjs(api?.gantt?.project?.startDate) : null}
          listeners={{
            change: ({ value, userAction }) => {
              if(userAction){
                onProjectStartChange(value);
              }
            },
          }}
        />

        {!readOnly && (
          <Tooltip title={translate("Sync project with ridder")}>
            <Button onClick={onSync} size="small" className={css.iconBtn} data-testid="sync-project-button">
              <span className="ico icon-tab deg-90" />
            </Button>
          </Tooltip>
        )}
        <UsersOnPageBtn users={users} />
        {allowBulldoze && (<Tooltip title={translate("processProgressTitle")}>
          <Button onClick={processProgress} size="small" className={css.iconBtn} data-testid="progress-button">
            <span className="b-fa b-fa-bars-progress" />
          </Button>
        </Tooltip>)}
        <ToggleVisibilityButtons hideGantt hideScheduler configs={configs} overview={overview} api={api} />
        <BaselineButtons overview={overview} readOnly={ readOnly} />
        <ExportButton overview={overview} api={api} allowMSPExport={true} />
        <SaveSettings configs={configs} api={api} groupKey={groupNames.SINGLE_PROJECT_GANTT_CONFIG} />
      </div>
    </div>
  );
}
